import React, { ChangeEvent } from 'react';
import Logger from '../../../toolkit/logger/Logger';
import { RouteComponentProps } from 'react-router-dom';
import { num } from '../../../utils/easy';

export type Props = Partial<RouteComponentProps>;

export interface DataTableState {
  search: string | null;
  sort: {
    key: string | null,
    type: 'asc' | 'desc' | string;
  };
  pagination: {
    max: number;
    limit: number;
    current: number;
  }
}

abstract class DataTable<P extends Props = {}, S extends Partial<DataTableState> = {}, SS = any> extends React.Component<P, S, SS> {

  protected unregisterHistory = null;

  state = {
    search: '',
    sort: {
      key: null,
      type: 'asc'
    },
    pagination: {
      max: 1,
      limit: 10,
      current: 1
    }
  } as any;

  protected abstract async fetchData(): Promise<void>;

  public constructor(props: P) {
    super(props);
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('page')) {
      this.state.pagination.current = num(urlParams.get('page'), 1);
    }
    if (urlParams.has('q')) {
      this.state.search = urlParams.get('q');
    }
    this.registerHistoryListener();
  }

  async componentDidMount() {
    Logger.info('DataTable::ComponentDidMount called');
    await this.fetchData();
    const pagination = this.state.pagination;
    this.queryParameterCheck();
    if (pagination.current > pagination.max) {
      const update = ({ ...pagination });
      update.current = 1;
      this.setState({ pagination: update }, this.fetchData);
    }
  }

  protected onPaginationChange = (index: number) => {
    Logger.info(`Pagination::onPaginationChange(index=${index})`);
    const pagination = { ...this.state.pagination };
    pagination.current = index;
    this.setState({ pagination }, this.fetchData);
  }

  protected onSortTableChange = (key: string, type: string) => {
    Logger.info(`Table::onSortTableChange(key=${key}, type=${type}`);
    this.setState({ sort: { key, type } }, this.fetchData);
  }

  protected onSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    Logger.log(`Input::onSearchTextChange(value=${event.target.value})`);
    const pagination = { ...this.state.pagination }
    pagination.current = 1;
    this.setState({ search: event.target.value, pagination }, this.fetchData);
  }

  protected queryParameterCheck = () => {
    const params = new URLSearchParams(window.location.search);
    const updateState: any = {};
    if (params.has('page')) {
      Logger.warning('queryParameterCheck has page');
      const queryPage = num(params.get('page'), 1);
      if (queryPage !== this.state.pagination.current) {
        const pagination = { ...this.state.pagination };
        pagination.current = queryPage;
        updateState.pagination = pagination;
      }
    }
    if (Object.keys(updateState).length > 0) {
      this.setState(updateState, this.fetchData);
    }
  }

  protected registerHistoryListener = () => {
    this.unregisterHistory = this.props.history.listen(() => {
      const action = this.props.history.action;
      Logger.info(`BrowserHistory::Action(${action})`);
      if (action === 'POP') {
        this.queryParameterCheck();
      }
    });
  }

  public componentWillUnmount() {
    this.unregisterHistory();
  }

}

export default DataTable;
